/**
 * DismissButton — A small ✕ button that appears near the avatar when
 * a vision-loop suggestion is shown. DOM element for reliable click handling.
 *
 * Styled as a glassmorphic 28px circle, consistent with widget controls.
 * Positioned at the top-right of the avatar area.
 */

export class DismissButton {
  private button: HTMLButtonElement | null = null

  /** Called when the button is clicked. */
  onClick: (() => void) | null = null

  /** Show the dismiss button. */
  show(): void {
    if (this.button) return

    this.button = document.createElement('button')
    this.button.textContent = '\u00D7' // ×
    this.button.setAttribute('aria-label', 'Dismiss suggestion')
    Object.assign(this.button.style, {
      position: 'fixed',
      bottom: '260px',
      left: '50%',
      transform: 'translateX(50px)',
      zIndex: '2147483647',
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      border: '1px solid rgba(200, 208, 218, 0.6)',
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      color: '#4a4a6a',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      lineHeight: '1',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      pointerEvents: 'auto',
      opacity: '0',
      transition: 'opacity 0.3s ease, background 0.15s, transform 0.15s',
    })

    this.button.addEventListener('click', (e) => {
      e.stopPropagation()
      this.onClick?.()
    })

    this.button.addEventListener('mouseenter', () => {
      if (this.button) {
        this.button.style.background = 'rgba(240, 242, 245, 0.95)'
        this.button.style.transform = 'translateX(50px) scale(1.1)'
      }
    })
    this.button.addEventListener('mouseleave', () => {
      if (this.button) {
        this.button.style.background = 'rgba(255, 255, 255, 0.85)'
        this.button.style.transform = 'translateX(50px) scale(1)'
      }
    })

    // Append inside the widget root so rrweb's blockSelector excludes it
    const root = document.getElementById('holostaff-widget-root')
    ;(root ?? document.body).appendChild(this.button)

    // Fade in
    requestAnimationFrame(() => {
      if (this.button) this.button.style.opacity = '1'
    })
  }

  /** Hide and remove the dismiss button. */
  hide(): void {
    if (!this.button) return
    this.button.remove()
    this.button = null
  }
}
